import Link from 'next/link';

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  published: boolean;
  featured: boolean;
  state: string;
  typeArticle: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  viewCount: number;
  readTime?: number;
  featuredImage?: string;
  featuredImageAlt?: string;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string;
  author: {
    id: string;
    name: string;
    email: string;
  };
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  tags: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
}

interface ArticleCardProps {
  article: Article;
  lang: string;
}

export default function ArticleCard({ article, lang }: ArticleCardProps) {
  const emptyImg = '/files/images/pages/empty_img_article.jpg';
  const featuredImageSrc = typeof article.featuredImage === 'string' && article.featuredImage.trim().length > 0
    ? article.featuredImage
    : emptyImg;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(lang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getTypeLabel = (type: string) => {
    const typeLabels: Record<string, string> = {
      NEWS: 'News',
      ANNOUNCEMENT: 'Announcement',
      GUIDE: 'Guide',
      TUTORIAL: 'Tutorial',
      BLOG: 'Blog',
      PRESS_RELEASE: 'Press Release',
      RESEARCH: 'Research',
      REPORT: 'Report',
      EVENT: 'Event',
      OTHER: 'Other',
      PAGE: 'Page',
      POST: 'Post',
    };

    return typeLabels[type] || type;
  };

  return (
    <article className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200">
      <div className="aspect-w-16 aspect-h-9">
        <img
          src={featuredImageSrc}
          alt={article.featuredImageAlt || article.title}
          className="w-full h-60 object-cover"
          onError={(event) => {
            event.currentTarget.src = emptyImg;
          }}
        />
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {article.featured && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                Featured
              </span>
            )}
            {article.typeArticle && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                {getTypeLabel(article.typeArticle)}
              </span>
            )}
          </div>
          {article.readTime && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{article.readTime} min read</span>
          )}
        </div>

        {article.category && (
          <div className="mb-3">
            <Link
              href={`/${lang}/articles/category/${article.category.slug}`}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
            >
              {article.category.name}
            </Link>
          </div>
        )}

        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 line-clamp-2">
          <Link
            href={`/${lang}/article/${article.slug}`}
            className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            {article.title}
          </Link>
        </h2>

        {article.excerpt && (
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">{article.excerpt}</p>
        )}

        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-2">
            <span>{article.author.name}</span>
            <span>•</span>
            <time dateTime={article.publishedAt || article.createdAt}>
              {formatDate(article.publishedAt || article.createdAt)}
            </time>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span>{article.viewCount.toLocaleString()}</span>
          </div>
        </div>

        {article.tags && article.tags.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-1">
              {article.tags.slice(0, 3).map((tag) => (
                <Link
                  key={tag.id}
                  href={`/${lang}/articles/tag/${tag.slug}`}
                  className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  #{tag.name}
                </Link>
              ))}
              {article.tags.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-500 dark:text-gray-400">
                  +{article.tags.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
